/**
 * ObjectiveSetupPage
 *
 * Bifurcación post-creación: el usuario elige cómo quiere terminar de
 * configurar la campaña.
 *
 *   Manual → control total (3 pasos visuales)
 *   Auto   → la IA llena todo y el usuario aprueba
 *
 * También sirve de "reanudar configuración" para campañas draft que
 * el usuario abandonó a mitad de camino.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Edit3,
  BrainCircuit,
  Sparkles,
  Loader2,
} from "lucide-react";
import { objectivesApi } from "../api";
import type { Objective } from "../types";
import { PRESET_BY_KEY } from "../types";

export function ObjectiveSetupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [objective, setObjective] = useState<Objective | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const res = await objectivesApi.getById(id);
      if (res.success) setObjective(res.data);
      else setError(res.error || "No se pudo cargar la campaña");
      setLoading(false);
    })();
  }, [id]);

  /* ─── Decide mode ─── */
  const handlePickMode = async (mode: "manual" | "auto") => {
    if (!id) return;
    // Persist the setupMode so we know which path the user chose
    await objectivesApi.update(id, { setupMode: mode });
    navigate(`/objectives/${id}/setup/${mode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando campaña…</span>
        </div>
      </div>
    );
  }

  if (error || !objective) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-red-700 font-medium mb-3">
            {error || "Campaña no encontrada"}
          </p>
          <Link
            to="/objectives"
            className="text-violet-600 text-sm hover:underline"
          >
            Volver a campañas
          </Link>
        </div>
      </div>
    );
  }

  const presetMeta = PRESET_BY_KEY[objective.preset];

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Breadcrumb */}
        <Link
          to="/objectives"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <ArrowLeft size={14} />
          Volver a campañas
        </Link>

        {/* Header con contexto de la campaña */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-start gap-4">
            <div className="text-5xl leading-none flex-shrink-0">
              {objective.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wider">
                  Paso 2: Configuración
                </span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                {objective.name}
              </h1>
              <p className="text-slate-500 mt-1 leading-relaxed">
                {presetMeta.longDescription}
              </p>
            </div>
          </div>
        </div>

        {/* Question */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">
            ¿Cómo querés configurarlo?
          </h2>
          <p className="text-slate-500">
            Podés tomar las riendas o dejar que la IA proponga todo y vos
            aprobás.
          </p>
        </div>

        {/* Two options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Manual */}
          <button
            onClick={() => handlePickMode("manual")}
            className="text-left group relative overflow-hidden bg-white rounded-2xl border-2 border-slate-200 hover:border-emerald-300 transition-all p-6 hover:shadow-md"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-100/40 rounded-full blur-[60px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="relative">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 mb-4 group-hover:scale-110 transition-transform">
                <Edit3 size={22} />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Configurarlo yo mismo
              </h3>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                Elegís la audiencia, la plantilla y la cadencia paso a paso.
                Mantenés el control total de cada decisión.
              </p>

              <ul className="space-y-1.5 text-xs text-slate-600 mb-5">
                <ListItem>3 pasos guiados</ListItem>
                <ListItem>Audiencia y plantilla a tu medida</ListItem>
                <ListItem>Ideal si ya sabés qué querés</ListItem>
              </ul>

              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm group-hover:gap-3 transition-all">
                Empezar configuración
                <ArrowRight size={15} />
              </div>
            </div>
          </button>

          {/* Auto */}
          <button
            onClick={() => handlePickMode("auto")}
            className="text-left group relative overflow-hidden bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-2xl border-2 border-transparent hover:scale-[1.01] transition-all p-6 shadow-lg shadow-violet-500/20 text-white"
          >
            <div className="absolute top-0 right-0 w-60 h-60 bg-white/10 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BrainCircuit size={22} />
              </div>

              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold">La IA lo hace por mí</h3>
                <span
                  className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-[10px] font-bold rounded-full uppercase tracking-wider"
                  title="La IA arma todo y vos aprobás — incluido en tu plan"
                >
                  Incluido
                </span>
              </div>
              <p className="text-sm text-white/80 mb-5 leading-relaxed">
                La IA analiza tus datos, propone el segmento, redacta la
                plantilla y sugiere el mejor horario. Vos solo aprobás o
                ajustás.
              </p>

              <ul className="space-y-1.5 text-xs text-white/80 mb-5">
                <ListItem variant="dark">Setup en 30 segundos</ListItem>
                <ListItem variant="dark">
                  Plantillas hiperpersonalizadas
                </ListItem>
                <ListItem variant="dark">
                  Cadencia optimizada por horario
                </ListItem>
              </ul>

              <div className="flex items-center gap-2 font-semibold text-sm group-hover:gap-3 transition-all">
                <Sparkles size={15} />
                Que la IA lo arme
                <ArrowRight size={15} />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-component ─── */
function ListItem({
  children,
  variant = "light",
}: {
  children: React.ReactNode;
  variant?: "light" | "dark";
}) {
  return (
    <li className="flex items-start gap-2">
      <span
        className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${
          variant === "light" ? "bg-slate-400" : "bg-white/60"
        }`}
      />
      <span>{children}</span>
    </li>
  );
}

export default ObjectiveSetupPage;
